import {
  forwardRef,
  useImperativeHandle,
  useRef,
  type ClipboardEvent,
  type KeyboardEvent,
} from 'react';
import { cn } from '../../utils/cn';
import { useI18n } from '@bravemove/i18n/react/core';

export interface OTPInputProps {
  readonly length?: number;
  readonly value: string;
  readonly onChange: (value: string) => void;
  /** Fires when the user enters the final digit. */
  readonly onComplete?: ((value: string) => void) | undefined;
  readonly autoFocus?: boolean;
  readonly invalid?: boolean;
  readonly disabled?: boolean;
  readonly inputMode?: 'numeric' | 'text';
  readonly className?: string;
  readonly ariaLabel?: string;
}

export interface OTPInputRef {
  readonly focus: () => void;
  readonly clear: () => void;
}

/**
 * OTP code input — 6 cells by default, numeric, auto-advance on keystroke,
 * back-step on Backspace, full-code paste-fill. Each cell is a single-char
 * input so screen readers + autofill behave correctly.
 */
export const OTPInput = forwardRef<OTPInputRef, OTPInputProps>(function OTPInput(
  {
    length = 6,
    value,
    onChange,
    onComplete,
    autoFocus = false,
    invalid,
    disabled,
    inputMode = 'numeric',
    className,
    ariaLabel,
  },
  ref,
) {
  const { t } = useI18n();
  const refs = useRef<(HTMLInputElement | null)[]>([]);

  useImperativeHandle(
    ref,
    () => ({
      focus: () => refs.current[0]?.focus(),
      clear: () => {
        onChange('');
        refs.current[0]?.focus();
      },
    }),
    [onChange],
  );

  const digits = Array.from({ length }, (_, i) => value.charAt(i) ?? '');

  const focusCell = (i: number) => {
    const el = refs.current[i];
    if (el) {
      el.focus();
      el.select();
    }
  };

  const setAt = (i: number, ch: string) => {
    const arr = digits.slice();
    arr[i] = ch;
    const next = arr.join('').slice(0, length);
    onChange(next);
    if (next.length === length && next.replace(/\s/g, '').length === length) {
      onComplete?.(next);
    }
  };

  const handleKey = (i: number) => (e: KeyboardEvent<HTMLInputElement>) => {
    // Preserve browser shortcuts such as copy, paste, select-all and autofill.
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (digits[i]) {
        setAt(i, '');
      } else if (i > 0) {
        setAt(i - 1, '');
        focusCell(i - 1);
      }
      return;
    }
    if (e.key === 'ArrowLeft' && i > 0) {
      e.preventDefault();
      focusCell(i - 1);
      return;
    }
    if (e.key === 'ArrowRight' && i < length - 1) {
      e.preventDefault();
      focusCell(i + 1);
      return;
    }
    // single-character input
    if (e.key.length === 1) {
      const ok = inputMode === 'numeric' ? /^\d$/.test(e.key) : /^[a-zA-Z0-9]$/.test(e.key);
      if (!ok) {
        e.preventDefault();
        return;
      }
      e.preventDefault();
      setAt(i, e.key);
      if (i < length - 1) focusCell(i + 1);
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const txt = e.clipboardData.getData('text').trim();
    if (!txt) return;
    e.preventDefault();
    const ok = inputMode === 'numeric' ? txt.replace(/\D/g, '') : txt.replace(/[^a-zA-Z0-9]/g, '');
    const next = ok.slice(0, length);
    onChange(next);
    if (next.length === length) {
      onComplete?.(next);
      refs.current[length - 1]?.focus();
    } else {
      refs.current[next.length]?.focus();
    }
  };

  return (
    <div
      role="group"
      aria-label={ariaLabel ?? t('common:otp.label', { defaultValue: 'One-time code' })}
      dir="ltr"
      style={{
        gridTemplateColumns: `repeat(${length}, minmax(0, 1fr))`,
        maxWidth: `calc(var(--spacing) * ${length * 12 + (length - 1) * 2})`,
      }}
      className={cn('grid w-full gap-1.5 sm:gap-2', disabled && 'opacity-45', className)}
    >
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(node) => {
            refs.current[i] = node;
          }}
          type="text"
          inputMode={inputMode}
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          maxLength={i === 0 ? length : 1}
          autoFocus={autoFocus && i === 0}
          disabled={disabled}
          value={d}
          aria-invalid={invalid || undefined}
          aria-label={t('common:otp.digit', { position: i + 1, defaultValue: `Digit ${i + 1}` })}
          onChange={(event) => {
            const raw = event.currentTarget.value;
            const cleaned =
              inputMode === 'numeric' ? raw.replace(/\D/g, '') : raw.replace(/[^a-zA-Z0-9]/g, '');
            if (cleaned.length > 1) {
              const next = cleaned.slice(0, length);
              onChange(next);
              if (next.length === length) onComplete?.(next);
              focusCell(Math.min(next.length, length - 1));
            } else {
              setAt(i, cleaned);
              if (cleaned && i < length - 1) focusCell(i + 1);
            }
          }}
          onKeyDown={handleKey(i)}
          onPaste={handlePaste}
          onFocus={(e) => e.target.select()}
          className={cn(
            'w-full min-w-0 aspect-square max-h-12 rounded-lg text-center text-h3 font-bold tabular-nums',
            'bg-surface text-ink border border-hairline-2 caret-accent',
            'transition-[box-shadow,border-color] duration-(--duration-tap) ease-(--ease-out) motion-reduce:transition-none',
            'focus:outline-2 focus:outline-offset-2 focus:outline-accent-text focus:border-accent',
            'disabled:cursor-not-allowed',
            invalid && 'border-danger focus:border-danger focus:ring-danger/25',
          )}
        />
      ))}
    </div>
  );
});
